import { FlexBox, LoadingButton } from '@airship-platform/jam-web/components';
import { MailIcon, ShareIcon, SmsIcon } from '@airship-platform/jam-web/icons';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useFormContext } from 'react-hook-form';
import { type ContactInfo } from '../../../../data-access/appointments';
import { type FollowUpSectionInputs } from '../sections/FollowUpSection';
import CopyMessageButton from './CopyMessageButton';
import FollowUpChannelCard from './FollowUpChannelCard';
import {
  buildFollowUpBody,
  buildMailtoHref,
  buildSmsHref,
  FOLLOW_UP_SUBJECT,
  mobilePhonesOf,
  toPlainText,
} from './followUpMessage.utils';
import FollowUpPhonesInput from './FollowUpPhonesInput';
import NativeShareButton from './NativeShareButton';

export type FollowUpChannel = 'text' | 'email' | 'share' | 'copy';

interface OwnProps {
  contacts: ContactInfo[];
  isLoading: boolean;
  isLoadingChannel?: FollowUpChannel;
  getShareLink: (channel: FollowUpChannel) => Promise<string | null>;
}

const FollowUpNativeActions: React.FC<OwnProps> = ({
  contacts,
  isLoading,
  isLoadingChannel,
  getShareLink,
}) => {
  const { watch } = useFormContext<FollowUpSectionInputs>();

  const [message, emails, smsPhones] = watch([
    'followUpMessage',
    'emails',
    'smsPhones',
  ]);

  const phones = mobilePhonesOf(contacts);

  const getMessageBody = async (channel: FollowUpChannel) => {
    const shareLink = await getShareLink(channel);

    return shareLink ? buildFollowUpBody(message, shareLink) : null;
  };

  const openWith =
    (channel: FollowUpChannel, buildHref: (body: string) => string) =>
    async () => {
      const body = await getMessageBody(channel);

      if (body) {
        window.location.href = buildHref(body);
      }
    };

  return (
    <FlexBox flexDirection={'column'} gap={2} width={'100%'}>
      <Divider />

      <Typography variant="body2">Send from your own apps</Typography>

      <FollowUpChannelCard
        title={'Text message'}
        subtitle={
          phones.length
            ? 'Choose mobile numbers'
            : 'Add a mobile number to text'
        }
        icon={<SmsIcon width={20} height={20} />}
        action={
          <LoadingButton
            color={'softGold'}
            aria-label={'Send text message'}
            startIcon={<SmsIcon />}
            loading={isLoadingChannel === 'text'}
            disabled={!smsPhones.length}
            onClick={openWith('text', (body) => buildSmsHref(smsPhones, body))}
          >
            Send
          </LoadingButton>
        }
      >
        <FollowUpPhonesInput phones={phones} isLoading={isLoading} />
      </FollowUpChannelCard>

      <FollowUpChannelCard
        title={'Email'}
        subtitle={'Opens a draft to the addresses chosen above'}
        icon={<MailIcon width={20} height={20} />}
        action={
          <LoadingButton
            color={'softGold'}
            aria-label={'Send email'}
            startIcon={<MailIcon />}
            loading={isLoadingChannel === 'email'}
            disabled={!emails.length}
            onClick={openWith('email', (body) =>
              buildMailtoHref(emails, FOLLOW_UP_SUBJECT, body)
            )}
          >
            Send
          </LoadingButton>
        }
      />

      <FollowUpChannelCard
        title={'Share'}
        subtitle={'Pick any app on your device'}
        icon={<ShareIcon width={20} height={20} />}
        action={
          <FlexBox gap={1}>
            <CopyMessageButton
              getMessageBody={() => getMessageBody('copy')}
              loading={isLoadingChannel === 'copy'}
            />
            <NativeShareButton
              title={FOLLOW_UP_SUBJECT}
              text={toPlainText(message).trim()}
              getShareLink={() => getShareLink('share')}
              loading={isLoadingChannel === 'share'}
            />
          </FlexBox>
        }
      />
    </FlexBox>
  );
};

export default FollowUpNativeActions;
